import React from 'react';
import {
  faArrowsRotate,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import AssessmentCard from './AssessmentCard';

import { Assessment } from '@/services/assessmentsAPI';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import LoadingState from '@/components/LoadingState';
import Alert from '@/components/Alert';
import { Body, Title } from '@/components/Typography';

interface AssessmentsListProps {
  assessments: Assessment[];
  selectedAssessment: Assessment | null;
  onSelectAssessment: (assessment: Assessment) => void;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const AssessmentsList: React.FC<AssessmentsListProps> = ({
  assessments,
  selectedAssessment,
  onSelectAssessment,
  loading,
  error,
  onRefresh,
}) => {
  if (loading) {
    return (
      <Card>
        <LoadingState text="Loading assessments..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card changeStyle={{ padding: 24 }}>
        <Stack vertical gap={16} align="center">
          <Alert
            status="danger"
            icon={faExclamationTriangle}
            description={error}
          />
          <Button
            onClick={onRefresh}
            a11yLabel="Retry loading assessments"
            label="Retry"
          />
        </Stack>
      </Card>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card changeStyle={{ padding: 24 }}>
        <Stack vertical gap={16} align="center">
          <Body>No assessments found</Body>
          <Button
            onClick={onRefresh}
            a11yLabel="Refresh assessments list"
            label="Refresh"
          />
        </Stack>
      </Card>
    );
  }

  return (
    <Card changeStyle={{ padding: 24 }}>
      <Stack vertical gap={16} css={{ height: '100%' }}>
        <Stack justify="apart" align="center">
          <Title>Assessments ({assessments.length})</Title>
          <Button
            onClick={onRefresh}
            a11yLabel="Refresh assessments list"
            iconLeft={faArrowsRotate}
            label="Refresh"
          />
        </Stack>
        <Stack vertical gap={8} css={{ overflowY: 'auto', flex: 1 }}>
          {assessments.map((assessment) => (
            <AssessmentCard
              key={assessment.external_id}
              assessment={assessment}
              isSelected={
                selectedAssessment?.external_id === assessment.external_id
              }
              onClick={() => onSelectAssessment(assessment)}
            />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};

export default AssessmentsList;
